import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { NavigationService } from '@treasury/core/navigation';
import { ApprovalDecision } from '@treasury/domain/approvals/types';
import {
    DashboardService,
    PaymentType,
} from '@treasury/domain/channel/services/dashboard/dashboard-service';
import { AuditModel, Loan } from '@treasury/domain/loans';
import { UsersService } from '@treasury/domain/users';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-footer';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import { format } from 'date-fns/format';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../../components/jhd-status';
import { showApproveRejectPrompt } from '../../pending-approvals/partials';

type LoanWithDecision<T> = Partial<T> & { selectedState: string };

export const tagName = 'loan-detail-bottom-sheet';
@customElement(tagName)
export class LoanDetailBottomSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public bottomSheetOpen = false;

    @property({ type: Boolean })
    public useNewFlow = false;

    @property()
    public loan: Loan = {} as Loan;

    @property({ type: Boolean, reflect: true, attribute: 'is-approving' })
    public isApproving = false;

    @InjectProperty()
    declare private dashboardService: DashboardService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private usersService: UsersService;

    @state()
    private auditOpen = false;

    @state()
    private approversOpen = false;

    @state()
    private completedApprovals: { dateApproved: string; name: string }[] = [];

    @state()
    private availableApprovers: string[] = [];

    @state()
    private loadingApprovals = false;

    @state()
    private loading = false;

    private get canEditLoan() {
        return (
            this.loan?.permissions.includes('Edit') &&
            (this.loan.status === 'Failed' ||
                this.loan.status === 'Scheduled' ||
                this.loan.status === 'ApprovalRejected' ||
                this.loan.status === 'Expired' ||
                (this.loan.status === 'Submitted' &&
                    new Date(this.loan.transferDate || '').toDateString() ===
                        new Date().toDateString()))
        );
    }

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.bottomSheetOpen}
            @close=${() => {
                this.close();
            }}
        >
            <span slot="header-center">Loan Detail</span>
            ${this.loading ? html`<tm-blocking-loader></tm-blocking-loader>` : nothing}
            ${this.isApproving ? this.renderApprovalInformation() : nothing}
            <tm-section>${this.renderLoanDetails()}</tm-section>
            ${this.isApproving ? nothing : this.renderApprovalInformation()}
            ${this.renderButtonBar()}
        </tm-bottom-sheet>`;
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('bottomSheetOpen') && this.bottomSheetOpen) {
            this.getApprovalInformation();
        }
    }

    private async approveOrReject({
        items,
        paymentType = 'loanPayment',
    }: {
        items: LoanWithDecision<Loan>[];
        paymentType: PaymentType;
    }) {
        try {
            this.loading = true;
            await this.dashboardService.approveOrRejectPayment(items, paymentType);
            this.notificationService.renderSuccess('Loan payment has been successfully updated');
            this.dispatchEvent(new CustomEvent('update-loan'));
            this.dispatchEvent(
                new CustomEvent('approve-or-reject', {
                    composed: true,
                    bubbles: true,
                    detail: { items, paymentType },
                })
            );
            this.close();
        } catch (err) {
            this.notificationService.renderError(err as Error);
        } finally {
            this.loading = false;
        }
    }

    private toggleAudit() {
        this.auditOpen = !this.auditOpen;
    }

    private close() {
        this.bottomSheetOpen = false;
        this.auditOpen = false;
        this.approversOpen = false;
        this.dispatchEvent(new CustomEvent('close'));
    }

    private renderStatus() {
        if (!this.loan) {
            return nothing;
        }
        return html`<jhd-status .status=${this.loan.status} showLabel></jhd-status>`;
    }

    private async getApprovalInformation() {
        if (this.loan?.status === 'PendingApproval') {
            this.loadingApprovals = true;
            const { completedApprovals, availableApprovers } =
                await this.usersService.fetchAvailableAndCompletedApprovers({
                    approvalEntity: 'internalTransfer',
                    productId: this.loan.id as number | undefined,
                    updatedBy: parseInt(this.loan.updatedBy),
                });
            this.loadingApprovals = false;
            this.completedApprovals = completedApprovals;
            this.availableApprovers = availableApprovers;
        }
    }

    private renderApprovalInformation() {
        if (this.loan?.status !== 'PendingApproval' || this.loading) {
            return nothing;
        }
        return html`<tm-section
            class=${classMap({
                'mt-3': !this.isApproving,
                'mb-3': this.isApproving,
            })}
            ><treasury-pending-approval-template
                .approvalItem=${this.loan}
                .approvals=${this.completedApprovals}
                class="pb-2"
                .loading=${this.loadingApprovals}
                .eligibleApprovers=${this.availableApprovers}
                .eligibleApproversVisible=${this.approversOpen}
                @eligible-approvers-visible=${(e: CustomEvent) => {
                    this.approversOpen = e.detail;
                }}
            ></treasury-pending-approval-template
        ></tm-section>`;
    }

    private renderLoanDetails() {
        if (!this.loan) {
            return nothing;
        }
        return html`<div class="detail-wrapper px-6 py-3 bg-primary">
            <div class="loan-detail-wrapper flex items-center justify-between">
                <div class="detail-amount">
                    <div class="text-lg font-medium mb-1">Loan</div>
                    <div class="text-3xl font-normal">
                        ${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        }).format(this.loan.amount)}
                    </div>
                </div>
                <div class="detail-status flex items-center my-1 text-xs">
                    ${this.renderStatus()}
                </div>
            </div>
            <div class="divider"></div>
            <jhd-data-field
                .label=${'From'}
                .value=${this.loan.fromAccount?.accountDisplayLabel}
            ></jhd-data-field>
            <jhd-data-field
                .label=${'To loan'}
                .value=${this.loan.toAccount?.accountDisplayLabel}
            ></jhd-data-field>
            <jhd-data-field .label=${'Type'} .value=${this.loan.paymentOptionType}></jhd-data-field>
            <jhd-data-field .label=${'Frequency'} .value=${'One Time'}></jhd-data-field>
            <jhd-data-field
                .label=${'Payment Date'}
                .value=${format(new Date(this.loan.transferDate || ''), 'P')}
            ></jhd-data-field>
            <jhd-data-field .label=${'End Date'} .value=${'none'}></jhd-data-field>
            <jhd-data-field .label=${'Memo'} .value=${this.loan.memo || 'none'}></jhd-data-field>
            <jhd-data-field
                .label=${'Transaction ID'}
                .value=${this.loan.transactionId}
            ></jhd-data-field>
            <jhd-data-field
                .label=${'Creation Date'}
                .value=${format(new Date(this.loan.createdDate || ''), 'P')}
            ></jhd-data-field>
            <jhd-data-field
                .label=${'Created By'}
                .value=${this.loan.audit[this.loan.audit.length - 1].createdBy}
            ></jhd-data-field>
            ${this.renderAudit()}
        </div>`;
    }

    private renderButtonBar() {
        let buttonConfig: ButtonConfig[] = [];
        if (this.canEditLoan) {
            buttonConfig.push({
                text: 'Edit',
                classes: 'w-full m-1',
                onClick: () => {
                    this.navService.navigate(`/loans/payment/edit/${this.loan?.id}`);
                },
            });
        }
        if (this.loan?.status === 'PendingApproval' && this.loan.permissions.includes('Approve')) {
            buttonConfig = [
                ...buttonConfig,
                {
                    error: true,
                    text: 'Reject',
                    classes: 'w-full m-1',
                    onClick: () => {
                        if (this.useNewFlow) {
                            this.dispatchEvent(
                                new CustomEvent('initiate-decision', {
                                    detail: ApprovalDecision.Reject,
                                })
                            );
                            return;
                        }
                        this.showApproveReject('reject');
                    },
                },
                {
                    success: true,
                    text: 'Approve',
                    classes: 'w-full m-1',
                    onClick: () => {
                        if (this.useNewFlow) {
                            this.dispatchEvent(
                                new CustomEvent('initiate-decision', {
                                    detail: ApprovalDecision.Approve,
                                })
                            );
                            return;
                        }
                        this.showApproveReject('approve');
                    },
                },
            ];
        }
        if (buttonConfig.length > 0) {
            return html` <tm-footer direction="row" .buttonConfig=${buttonConfig}> </tm-footer> `;
        }
        return nothing;
    }

    private renderAuditData() {
        if (!this.auditOpen) {
            return nothing;
        }
        const auditList = this.loan.audit.map(
            (auditItem: AuditModel) =>
                html`<div class="audit-item pb-2">
                    <span class="audit-date mr-1">${format(new Date(auditItem.date), 'Pp')}</span>
                    <span>${auditItem.createdBy} : ${auditItem.comments}</span>
                </div>`
        );
        return html`<div class="max-h-36 mt-6 pb-2">
            <div class="pb-2 font-medium">Audit</div>
            <div class="max-h-36 overflow-y-auto font-normal text-sm">${auditList}</div>
        </div>`;
    }

    private renderAudit() {
        if (!this.loan?.audit) {
            return nothing;
        }
        const auditButtonText = this.auditOpen ? 'Hide Audit' : 'View Audit';
        return html`
            ${this.renderAuditData()}
            <tm-button class="mt-2" @click=${() => this.toggleAudit()}>
                ${auditButtonText}
            </tm-button>
        `;
    }

    private async showApproveReject(action: 'approve' | 'reject') {
        const { reason, data: selectedState } = await showApproveRejectPrompt('loan', action);
        if (reason === DynamicComponentExitReason.Confirm) {
            this.approveOrReject({
                items: [
                    { ...this.loan.toDto(), selectedState },
                ] as unknown as LoanWithDecision<Loan>[],
                paymentType: 'loanpayment',
            });
        }
    }

    public static get styles() {
        return [
            css`
                .header-icon path {
                    fill: var(--content-light);
                }
                .status svg {
                    display: inline-block;
                }
                .status {
                    color: var(--header-text-color);
                }
                .divider,
                .audit-item {
                    border-bottom: 1px dashed var(--border-color);
                    color: var(--header-text-color);
                }
                .detail-amount,
                .status {
                    color: var(--header-text-color);
                }
                .audit-date {
                    color: var(--secondary-text-color, #444444);
                }
                .max-h-36 {
                    max-height: 9rem;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: LoanDetailBottomSheet;
    }
}
